import * as React from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";

interface ArticleRow {
  articleId: number;
  title: string;
  slug: string;
  topicName: string | null;
  isPublished: boolean;
  enableBotResponse: boolean;
  updatedAt: string;
  lastReviewedAt: string | null;
  publicViews: number;
  publicHelpful: number;
  publicNotHelpful: number;
  botHelpful: number;
  botNotHelpful: number;
  helpfulPercent: number | null;
  needsAttention: boolean;
}

interface Summary {
  totalArticles: number;
  publishedArticles: number;
  botEnabledArticles: number;
  totalViews: number;
  totalHelpful: number;
  totalNotHelpful: number;
  helpfulPercent: number | null;
  needingAttention: number;
}

type SortKey = "helpfulPercent" | "publicViews" | "botHelpful" | "updatedAt" | "needsAttention";

function HelpfulBadge({ pct, votes }: { pct: number | null; votes: number }) {
  if (pct === null || votes === 0) {
    return <span style={{ color: "var(--color-gray-400)", fontSize: 12 }}>No votes</span>;
  }
  const color = pct >= 70 ? "#16a34a" : pct >= 40 ? "#ca8a04" : "#dc2626";
  return (
    <span style={{ color, fontWeight: 600 }}>
      {pct}%
      <span style={{ color: "var(--color-gray-400)", fontWeight: 400, fontSize: 12, marginLeft: 4 }}>
        ({votes} vote{votes !== 1 ? "s" : ""})
      </span>
    </span>
  );
}

export default function KBPerformance() {
  const api = useApi();
  const [articles, setArticles] = React.useState<ArticleRow[]>([]);
  const [summary, setSummary] = React.useState<Summary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [days, setDays] = React.useState(30);
  const [sortKey, setSortKey] = React.useState<SortKey>("needsAttention");
  const [sortDesc, setSortDesc] = React.useState(true);
  const [filter, setFilter] = React.useState<"all" | "bot" | "attention">("all");

  React.useEffect(() => {
    load();
  }, [days]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [artRes, sumRes] = await Promise.all([
        api.admin.get(`api/kb/performance/articles?days=${days}`),
        api.admin.get(`api/kb/performance/summary`),
      ]);
      if (!artRes.ok) throw new Error(`Articles: ${artRes.status}`);
      if (!sumRes.ok) throw new Error(`Summary: ${sumRes.status}`);
      setArticles(await artRes.json());
      setSummary(await sumRes.json());
    } catch (err) {
      setError(`Failed to load: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDesc((d) => !d);
    else { setSortKey(key); setSortDesc(true); }
  }

  const filtered = articles.filter((a) => {
    if (filter === "bot") return a.enableBotResponse;
    if (filter === "attention") return a.needsAttention;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let av: number, bv: number;
    if (sortKey === "helpfulPercent") { av = a.helpfulPercent ?? 101; bv = b.helpfulPercent ?? 101; }
    else if (sortKey === "publicViews") { av = a.publicViews; bv = b.publicViews; }
    else if (sortKey === "botHelpful") { av = a.botHelpful + a.botNotHelpful; bv = b.botHelpful + b.botNotHelpful; }
    else if (sortKey === "updatedAt") { av = new Date(a.updatedAt).getTime(); bv = new Date(b.updatedAt).getTime(); }
    else { av = a.needsAttention ? 0 : 1; bv = b.needsAttention ? 0 : 1; }
    return sortDesc ? av - bv : bv - av;
  });

  const SortBtn = ({ k, label }: { k: SortKey; label: string }) => (
    <button
      onClick={() => toggleSort(k)}
      style={{
        background: "none", border: "none", cursor: "pointer",
        fontWeight: sortKey === k ? 700 : 400,
        color: sortKey === k ? "var(--color-primary)" : "inherit",
        padding: "0 4px", display: "flex", alignItems: "center", gap: 2
      }}
    >
      {label}
      {sortKey === k && <span>{sortDesc ? " ↑" : " ↓"}</span>}
    </button>
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1100 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>KB Performance</h1>
          <p style={{ margin: "4px 0 0", color: "var(--color-gray-500)", fontSize: 14 }}>
            Helpful/not-helpful rates from public site and bot conversations
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 13, color: "var(--color-gray-600)" }}>Period:</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ padding: "4px 8px", borderRadius: 4, border: "1px solid var(--color-gray-200)", fontSize: 13 }}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>
      </div>

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: "10px 14px", marginBottom: 20, color: "#991b1b" }}>
          {error}
        </div>
      )}

      {summary && (
        <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { label: "Total Articles", value: summary.totalArticles },
            { label: "Bot-Enabled", value: summary.botEnabledArticles },
            { label: "Total Views", value: summary.totalViews.toLocaleString() },
            {
              label: "Avg Helpful",
              value: summary.helpfulPercent !== null ? `${summary.helpfulPercent}%` : "—",
              accent: summary.helpfulPercent !== null && summary.helpfulPercent < 60
            },
            {
              label: "Need Attention",
              value: summary.needingAttention,
              accent: summary.needingAttention > 0
            },
          ].map((s) => (
            <div key={s.label} style={{
              background: "white", border: "1px solid var(--color-gray-100)",
              borderRadius: 8, padding: "14px 20px", minWidth: 130, boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
              <div style={{ fontSize: 12, color: "var(--color-gray-500)", marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.accent ? "#dc2626" : "inherit" }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {(["all", "bot", "attention"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: "5px 14px", borderRadius: 20, border: "1px solid",
              borderColor: filter === f ? "var(--color-primary)" : "var(--color-gray-200)",
              background: filter === f ? "var(--color-primary)" : "white",
              color: filter === f ? "white" : "var(--color-gray-700)",
              cursor: "pointer", fontSize: 13
            }}
          >
            {f === "all" ? "All Articles" : f === "bot" ? "Bot-Enabled Only" : "⚠ Needs Attention"}
          </button>
        ))}
        <span style={{ marginLeft: "auto", fontSize: 13, color: "var(--color-gray-400)", alignSelf: "center" }}>
          {sorted.length} article{sorted.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div style={{ background: "white", borderRadius: 8, border: "1px solid var(--color-gray-100)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--color-gray-100)", background: "var(--color-gray-50)" }}>
              <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600 }}>Article</th>
              <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600 }}>Topic</th>
              <th style={{ padding: "10px 8px", textAlign: "center" }}><SortBtn k="publicViews" label="Views" /></th>
              <th style={{ padding: "10px 8px", textAlign: "center" }}><SortBtn k="helpfulPercent" label="Helpful %" /></th>
              <th style={{ padding: "10px 8px", textAlign: "center" }}>Public</th>
              <th style={{ padding: "10px 8px", textAlign: "center" }}><SortBtn k="botHelpful" label="Bot Votes" /></th>
              <th style={{ padding: "10px 8px", textAlign: "center" }}>Bot</th>
              <th style={{ padding: "10px 8px", textAlign: "left" }}><SortBtn k="updatedAt" label="Updated" /></th>
              <th style={{ padding: "10px 14px" }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={9} style={{ padding: "40px 20px", textAlign: "center", color: "var(--color-gray-400)" }}>
                No articles found for this filter.
              </td></tr>
            )}
            {sorted.map((a) => {
              const totalVotes = a.publicHelpful + a.publicNotHelpful + a.botHelpful + a.botNotHelpful;
              const botVotes = a.botHelpful + a.botNotHelpful;
              return (
                <tr key={a.articleId} style={{
                  borderBottom: "1px solid var(--color-gray-100)",
                  background: a.needsAttention ? "#fff7ed" : "white"
                }}>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {a.needsAttention && <span title="Needs attention" style={{ color: "#f59e0b" }}>⚠</span>}
                      <div>
                        <div style={{ fontWeight: 500 }}>{a.title}</div>
                        <div style={{ fontSize: 11, color: "var(--color-gray-400)", marginTop: 1 }}>
                          {a.enableBotResponse ? "🤖 Bot-enabled" : "Public only"}
                          {!a.isPublished && " · Draft"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "10px 14px", color: "var(--color-gray-500)" }}>{a.topicName ?? "—"}</td>
                  <td style={{ padding: "10px 8px", textAlign: "center" }}>{a.publicViews.toLocaleString()}</td>
                  <td style={{ padding: "10px 8px", textAlign: "center" }}>
                    <HelpfulBadge pct={a.helpfulPercent} votes={totalVotes} />
                  </td>
                  <td style={{ padding: "10px 8px", textAlign: "center", fontSize: 12 }}>
                    <span style={{ color: "#16a34a" }}>👍 {a.publicHelpful}</span>
                    {" / "}
                    <span style={{ color: "#dc2626" }}>👎 {a.publicNotHelpful}</span>
                  </td>
                  <td style={{ padding: "10px 8px", textAlign: "center" }}>{botVotes > 0 ? botVotes : "—"}</td>
                  <td style={{ padding: "10px 8px", textAlign: "center", fontSize: 12 }}>
                    {botVotes > 0 ? (
                      <>
                        <span style={{ color: "#16a34a" }}>👍 {a.botHelpful}</span>
                        {" / "}
                        <span style={{ color: "#dc2626" }}>👎 {a.botNotHelpful}</span>
                      </>
                    ) : "—"}
                  </td>
                  <td style={{ padding: "10px 8px", color: "var(--color-gray-500)", fontSize: 12 }}>
                    {new Date(a.updatedAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <Link
                      to={`/kb/articles/${a.articleId}/edit`}
                      style={{ fontSize: 12, color: "var(--color-primary)", textDecoration: "none" }}
                    >
                      Edit
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
